import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, ArrowLeft, SignOut, Plus, PencilSimple, Trash, X, Package, ArrowClockwise, ImageSquare, CircleNotch, Star, Users, Briefcase, MagnifyingGlass, CheckCircle, DownloadSimple, ClipboardText } from "@phosphor-icons/react";
import {
  adminLogin, adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, uploadFile,
  adminGetCustomers, adminSetScopedItems, adminGetReorderList,
} from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { BUSINESS } from "@/lib/data";

const TOKEN_KEY = "amwap_admin_token";
const CATEGORIES = [
  "Hand Tools", "Power Tools", "Fasteners & Hardware", "Electrical", "Plumbing",
  "Paint & Sundries", "Safety & PPE", "Adhesives & Sealants", "Building Materials",
  "Cleaning & Janitorial", "HVAC", "Material Handling", "Abrasives", "Lighting",
];
const STOCK = ["In stock", "Backorder", "Out of stock"];
const empty = {
  name: "", sku: "", category: "Hand Tools", subcategory: "", brand: "", description: "", specs: "",
  unit: "each", price: "", call_for_pricing: false, stock_status: "In stock", stock_qty: "", low_stock_threshold: 5, enforce_stock: false, reorder_target: "", images: [],
  min_order_qty: 1, tiered_pricing: [], featured: false, active: true,
};
const field = "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600 transition";
const label = "block text-sm font-semibold text-navy-900 mb-1.5";

export default function AdminSupply() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | product | 'new'
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const imgInputRef = useRef(null);
  const [tab, setTab] = useState("products");
  const [customers, setCustomers] = useState([]);
  const [assigning, setAssigning] = useState(null); // customer being assigned
  const [selIds, setSelIds] = useState([]);
  const [scopePrices, setScopePrices] = useState({});
  const [prodSearch, setProdSearch] = useState("");
  const [savingScope, setSavingScope] = useState(false);
  const [reorder, setReorder] = useState([]);

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setProducts([]); setCustomers([]); setReorder([]); }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [p, c, r] = await Promise.all([adminGetProducts(t), adminGetCustomers(t).catch(() => []), adminGetReorderList(t).catch(() => [])]);
      setProducts(p);
      setCustomers(c);
      setReorder(r);
    }
    catch (e) { if (e.response?.status === 401) { toast.error("Session expired."); logout(); } else toast.error("Could not load products."); }
    finally { setLoading(false); }
  }, [logout]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const exportCustomers = () => {
    if (!customers.length) { toast.error("No customers to export."); return; }
    downloadCsv("amwap_customers", [
      { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "company", label: "Company" },
      { label: "Scoped items", get: (c) => (c.scoped_items || []).length },
    ], customers);
    toast.success("Customers exported.");
  };

  const exportReorder = () => {
    if (!reorder.length) { toast.error("Nothing to reorder right now."); return; }
    downloadCsv("amwap_reorder_list", [
      { key: "name", label: "Product" }, { key: "sku", label: "SKU" }, { key: "category", label: "Category" },
      { key: "stock_qty", label: "On hand" }, { key: "low_stock_threshold", label: "Alert below" },
      { key: "reorder_target", label: "Target" }, { key: "reorder_qty", label: "Order qty" }, { key: "unit", label: "Unit" },
    ], reorder);
    toast.success("Reorder list exported.");
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const t = await adminLogin(passcode); localStorage.setItem(TOKEN_KEY, t); setToken(t); setPasscode(""); toast.success("Welcome back."); }
    catch { toast.error("Incorrect passcode."); }
    finally { setLoading(false); }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ ...empty, ...p, price: p.price == null ? "" : p.price, reorder_target: p.reorder_target == null ? "" : p.reorder_target, tiered_pricing: p.tiered_pricing || [], images: p.images || [] });
    setEditing(p);
  };

  const addImages = async (files) => {
    if (!files?.length) return;
    setImgUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name} isn't an image.`); continue; }
        const data = await uploadFile(f);
        uploaded.push(data.url);
      }
      setForm((p) => ({ ...p, images: [...p.images, ...uploaded] }));
    } catch { toast.error("Image upload failed."); }
    finally { setImgUploading(false); }
  };

  const removeImage = (idx) => setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));

  const addTier = () => setForm((p) => ({ ...p, tiered_pricing: [...p.tiered_pricing, { min_qty: "", price: "" }] }));
  const updTier = (i, k, v) => setForm((p) => ({ ...p, tiered_pricing: p.tiered_pricing.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) }));
  const removeTier = (i) => setForm((p) => ({ ...p, tiered_pricing: p.tiered_pricing.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (imgUploading) { toast.error("Wait for images to finish uploading."); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: form.call_for_pricing || form.price === "" ? null : Number(form.price),
      min_order_qty: Number(form.min_order_qty) || 1,
      stock_qty: form.stock_qty === "" || form.stock_qty === null ? null : Number(form.stock_qty),
      low_stock_threshold: Number(form.low_stock_threshold) || 5,
      reorder_target: form.reorder_target === "" || form.reorder_target === null ? null : Number(form.reorder_target),
      tiered_pricing: form.tiered_pricing
        .filter((t) => t.min_qty !== "" && t.price !== "")
        .map((t) => ({ min_qty: Number(t.min_qty), price: Number(t.price) })),
    };
    try {
      if (editing === "new") { await adminCreateProduct(token, payload); toast.success("Product added."); }
      else { await adminUpdateProduct(token, editing.id, payload); toast.success("Product updated."); }
      setEditing(null); load(token);
    } catch { toast.error("Could not save product."); }
    finally { setSaving(false); }
  };

  const quickToggle = async (p, key) => {
    try { await adminUpdateProduct(token, p.id, { ...p, price: p.price, [key]: !p[key] }); load(token); }
    catch { toast.error("Could not update."); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await adminDeleteProduct(token, p.id); toast.success("Deleted."); load(token); }
    catch { toast.error("Could not delete."); }
  };

  const openAssign = (c) => { setAssigning(c); setSelIds(c.scoped_items || []); setScopePrices(c.scoped_prices || {}); setProdSearch(""); };
  const toggleSel = (id) => setSelIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const saveScope = async () => {
    setSavingScope(true);
    try {
      const prices = {};
      for (const id of selIds) {
        const v = scopePrices[id];
        if (v !== undefined && v !== "" && v !== null) prices[id] = Number(v);
      }
      await adminSetScopedItems(token, assigning.id, selIds, prices);
      toast.success(`Scoped items updated for ${assigning.name || assigning.email}.`);
      setAssigning(null);
      load(token);
    } catch { toast.error("Could not save scoped items."); }
    finally { setSavingScope(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-navy-900 grain flex items-center justify-center px-5" data-testid="supply-admin-login">
        <div className="relative z-10 w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="w-14 h-14 rounded-full bg-white grid place-items-center overflow-hidden"><img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" /></span>
          </div>
          <form onSubmit={handleLogin} className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-navy-900 text-white grid place-items-center mb-5"><LockKey size={22} weight="fill" /></div>
            <h1 className="font-display font-black text-2xl text-navy-900 tracking-tight">AMWAP Supply Admin</h1>
            <p className="text-slate-500 mt-2 text-sm">Enter your passcode to manage the catalog.</p>
            <input type="password" data-testid="supply-admin-passcode" value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="Passcode" autoFocus className="w-full mt-6 rounded-xl border border-slate-300 px-4 py-3.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600" />
            <button type="submit" disabled={loading || !passcode} data-testid="supply-admin-login-submit" className="w-full mt-4 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">{loading ? "Checking…" : "Unlock"}</button>
            <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-navy-900"><ArrowLeft size={16} /> Back to website</Link>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="supply-admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center overflow-hidden"><img src={BUSINESS.logo} alt="AMWAP" className="w-full h-full object-contain p-0.5" /></span>
            <span className="font-display font-extrabold text-navy-900">AMWAP Supply — Catalog</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} data-testid="supply-refresh" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><ArrowClockwise size={16} /> Refresh</button>
            <button onClick={logout} data-testid="supply-admin-logout" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-4 py-2 text-sm font-bold hover:bg-navy-600"><SignOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center gap-2 mb-8">
          <button onClick={() => setTab("products")} data-testid="tab-products" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "products" ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}><Briefcase size={16} weight="fill" /> Products ({products.length})</button>
          <button onClick={() => setTab("customers")} data-testid="tab-customers" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "customers" ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}><Users size={16} weight="fill" /> Customers ({customers.length})</button>
          <button onClick={() => setTab("reorder")} data-testid="tab-reorder" className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === "reorder" ? "bg-navy-900 text-white" : "bg-white border border-slate-300 text-navy-900 hover:bg-slate-100"}`}><ClipboardText size={16} weight="fill" /> Reorder ({reorder.length})</button>
        </div>

        {tab === "products" && (
        <>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-black text-xl text-navy-900">Products ({products.length})</h2>
          <button onClick={openNew} data-testid="new-product-btn" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-5 py-3 font-bold hover:bg-navy-600 transition-colors"><Plus size={18} weight="bold" /> New product</button>
        </div>

        {loading ? <p className="text-slate-500">Loading…</p> : products.length === 0 ? (
          <p className="text-slate-500" data-testid="admin-no-products">No products yet. Click "New product" to stock the catalog.</p>
        ) : (
          <div className="space-y-3">
            {products.map((p, i) => (
              <div key={p.id} data-testid={`admin-product-${i}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-slate-50 border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                  {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-contain p-1.5" /> : <Package size={24} weight="thin" className="text-slate-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-navy-900 truncate">{p.name}</h3>
                    {p.featured && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Featured</span>}
                    {!p.active && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Hidden</span>}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{p.category}{p.brand ? ` · ${p.brand}` : ""}{p.sku ? ` · SKU ${p.sku}` : ""} · {p.call_for_pricing || p.price == null ? "Call for pricing" : `$${Number(p.price).toFixed(2)}`}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => quickToggle(p, "featured")} data-testid={`feature-product-${i}`} aria-label="Feature" className={`w-9 h-9 grid place-items-center rounded-full border ${p.featured ? "border-amber-300 bg-amber-50 text-amber-600" : "border-slate-300 text-slate-500 hover:bg-slate-100"}`}><Star size={16} weight={p.featured ? "fill" : "regular"} /></button>
                  <button onClick={() => quickToggle(p, "active")} data-testid={`toggle-product-${i}`} className="text-xs font-bold rounded-full border border-slate-300 px-3 py-2 text-navy-900 hover:bg-slate-100">{p.active ? "Hide" : "Show"}</button>
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${i}`} aria-label="Edit" className="w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100"><PencilSimple size={16} /></button>
                  <button onClick={() => remove(p)} data-testid={`delete-product-${i}`} aria-label="Delete" className="w-9 h-9 grid place-items-center rounded-full border border-red-300 text-red-600 hover:bg-red-500 hover:text-white transition-colors"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {tab === "customers" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-black text-xl text-navy-900">Customers ({customers.length})</h2>
              <button onClick={exportCustomers} data-testid="export-customers" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy-900 hover:bg-slate-100"><DownloadSimple size={16} weight="bold" /> Export CSV</button>
            </div>
            {customers.length === 0 ? (
              <p className="text-slate-500" data-testid="admin-no-customers">No customer accounts yet. Customers who register at /account appear here so you can scope items for them.</p>
            ) : (
              <div className="space-y-3">
                {customers.map((c, i) => (
                  <div key={c.id} data-testid={`admin-customer-${i}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full bg-navy-900 text-white grid place-items-center shrink-0"><Users size={20} weight="fill" /></div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display font-bold text-navy-900 truncate">{c.name || c.email}{c.company ? ` · ${c.company}` : ""}</h3>
                      <p className="text-sm text-slate-500 truncate">{c.email} · {(c.scoped_items || []).length} scoped item{(c.scoped_items || []).length !== 1 ? "s" : ""}</p>
                    </div>
                    <button onClick={() => openAssign(c)} data-testid={`assign-customer-${i}`} className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-5 py-2.5 text-sm font-bold hover:bg-navy-600 transition-colors shrink-0"><Package size={16} weight="fill" /> Scope items</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "reorder" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-black text-xl text-navy-900">Reorder list ({reorder.length})</h2>
              <button onClick={exportReorder} data-testid="export-reorder" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-navy-900 hover:bg-slate-100"><DownloadSimple size={16} weight="bold" /> Export CSV</button>
            </div>
            <p className="text-sm text-slate-500 mb-6">Items at or below their low-stock alert. "Order qty" is the suggested amount to buy to reach the reorder target (defaults to 2× the alert level when no target is set).</p>
            {loading ? <p className="text-slate-500">Loading…</p> : reorder.length === 0 ? (
              <p className="text-slate-500" data-testid="admin-no-reorder">Everything's well stocked — nothing to reorder right now.</p>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-navy-500 uppercase text-[11px] tracking-[0.1em]">
                    <tr>
                      <th className="text-left px-4 py-3 font-bold">Product</th>
                      <th className="text-center px-4 py-3 font-bold">On hand</th>
                      <th className="text-center px-4 py-3 font-bold">Alert ≤</th>
                      <th className="text-center px-4 py-3 font-bold">Target</th>
                      <th className="text-right px-4 py-3 font-bold">Order qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reorder.map((r, i) => (
                      <tr key={r.id} data-testid={`reorder-row-${i}`} className="border-t border-slate-100">
                        <td className="px-4 py-3">
                          <span className="font-semibold text-navy-900">{r.name}</span>
                          <span className="block text-xs text-slate-400">{r.category}{r.sku ? ` · SKU ${r.sku}` : ""}</span>
                        </td>
                        <td className={`text-center px-4 py-3 font-bold ${r.stock_qty === 0 ? "text-red-600" : "text-orange-600"}`}>{r.stock_qty}</td>
                        <td className="text-center px-4 py-3 text-slate-500">{r.low_stock_threshold}</td>
                        <td className="text-center px-4 py-3 text-slate-500">{r.reorder_target}</td>
                        <td className="text-right px-4 py-3 font-black text-navy-900">{r.reorder_qty} {r.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {assigning && (
        <div className="fixed inset-0 z-[70] bg-navy-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onMouseDown={() => setAssigning(null)} data-testid="scope-editor">
          <div className="bg-white rounded-3xl w-full max-w-2xl p-6 md:p-8 relative my-8" onMouseDown={(e) => e.stopPropagation()}>
            <button onClick={() => setAssigning(null)} data-testid="scope-editor-close" aria-label="Close" className="absolute top-5 right-5 w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100"><X size={18} /></button>
            <h3 className="font-display font-black text-2xl text-navy-900 tracking-tight">Scope items</h3>
            <p className="text-slate-500 mt-1 text-sm">Select the products {assigning.name || assigning.email} will see up front. Selected: {selIds.length}</p>
            <div className="relative mt-4">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="scope-search" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="Search products…" className="w-full rounded-xl border border-slate-300 pl-10 pr-4 py-2.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600" />
            </div>
            <div className="mt-4 max-h-[50vh] overflow-y-auto space-y-2 pr-1">
              {products
                .filter((p) => !prodSearch || `${p.name} ${p.sku} ${p.brand} ${p.category}`.toLowerCase().includes(prodSearch.toLowerCase()))
                .map((p, i) => {
                  const on = selIds.includes(p.id);
                  return (
                    <div key={p.id} className={`rounded-xl border p-3 transition-colors ${on ? "border-navy-900 bg-navy-50" : "border-slate-200"}`}>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => toggleSel(p.id)} data-testid={`scope-product-${i}`} className="flex items-center gap-3 text-left flex-1 min-w-0">
                          <div className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${on ? "bg-navy-900 text-white" : "border border-slate-300"}`}>{on && <CheckCircle size={16} weight="fill" />}</div>
                          <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                            {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-contain p-1" /> : <Package size={16} className="text-slate-300" />}
                          </div>
                          <div className="min-w-0"><p className="font-semibold text-navy-900 truncate text-sm">{p.name}</p><p className="text-xs text-slate-500 truncate">{p.category}{p.price != null ? ` · list $${Number(p.price).toFixed(2)}` : " · call for pricing"}</p></div>
                        </button>
                        {on && (
                          <div className="shrink-0 flex items-center gap-1">
                            <span className="text-slate-400 text-sm">$</span>
                            <input
                              type="number" step="0.01" min="0"
                              data-testid={`scope-price-${i}`}
                              value={scopePrices[p.id] ?? ""}
                              onChange={(e) => setScopePrices((cur) => ({ ...cur, [p.id]: e.target.value }))}
                              placeholder={p.price != null ? Number(p.price).toFixed(2) : "price"}
                              className="w-24 rounded-lg border border-slate-300 px-2.5 py-2 text-sm text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            <button onClick={saveScope} disabled={savingScope} data-testid="scope-save" className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">{savingScope ? "Saving…" : `Save ${selIds.length} item${selIds.length !== 1 ? "s" : ""}`}</button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[70] bg-navy-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onMouseDown={() => setEditing(null)} data-testid="product-editor">
          <div className="bg-white rounded-3xl w-full max-w-2xl p-6 md:p-8 relative my-8" onMouseDown={(e) => e.stopPropagation()}>
            <button onClick={() => setEditing(null)} data-testid="product-editor-close" aria-label="Close" className="absolute top-5 right-5 w-9 h-9 grid place-items-center rounded-full border border-slate-300 text-navy-900 hover:bg-slate-100"><X size={18} /></button>
            <h3 className="font-display font-black text-2xl text-navy-900 tracking-tight">{editing === "new" ? "New product" : "Edit product"}</h3>
            <form onSubmit={save} className="mt-5 space-y-4">
              <div><label className={label}>Name *</label><input required data-testid="p-name" className={field} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>SKU</label><input data-testid="p-sku" className={field} value={form.sku} onChange={(e) => set("sku", e.target.value)} /></div>
                <div><label className={label}>Brand</label><input data-testid="p-brand" className={field} value={form.brand} onChange={(e) => set("brand", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Category *</label>
                  <input required list="cat-list" data-testid="p-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)} />
                  <datalist id="cat-list">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div><label className={label}>Subcategory</label><input data-testid="p-subcategory" className={field} value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} /></div>
              </div>

              {/* Images */}
              <div>
                <label className={label}>Images</label>
                <input ref={imgInputRef} type="file" accept="image/*" multiple className="hidden" data-testid="p-image-input" onChange={(e) => { addImages(Array.from(e.target.files || [])); e.target.value = ""; }} />
                <div className="flex flex-wrap gap-3">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                      <img src={img} alt="" className="w-full h-full object-contain p-1.5" />
                      <button type="button" onClick={() => removeImage(i)} aria-label="Remove image" className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-navy-900/80 text-white"><X size={11} weight="bold" /></button>
                    </div>
                  ))}
                  <button type="button" data-testid="p-add-image" onClick={() => imgInputRef.current?.click()} disabled={imgUploading} className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 grid place-items-center text-slate-400 hover:border-navy-600 hover:text-navy-600 disabled:opacity-60">
                    {imgUploading ? <CircleNotch size={20} className="animate-spin" /> : <ImageSquare size={22} />}
                  </button>
                </div>
              </div>

              <div><label className={label}>Description</label><textarea rows={3} data-testid="p-description" className={field} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
              <div><label className={label}>Specifications (one per line)</label><textarea rows={3} data-testid="p-specs" className={field} placeholder={"Voltage: 20V\nWeight: 3.2 lbs"} value={form.specs} onChange={(e) => set("specs", e.target.value)} /></div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-3">
                <div><label className={label}>Price ($)</label><input type="number" step="0.01" data-testid="p-price" disabled={form.call_for_pricing} className={`${field} disabled:bg-slate-100`} value={form.price} onChange={(e) => set("price", e.target.value)} /></div>
                <div><label className={label}>Unit</label><input data-testid="p-unit" className={field} placeholder="each / box / case" value={form.unit} onChange={(e) => set("unit", e.target.value)} /></div>
                <div><label className={label}>Min order qty</label><input type="number" min="1" data-testid="p-moq" className={field} value={form.min_order_qty} onChange={(e) => set("min_order_qty", e.target.value)} /></div>
              </div>
              <label className="flex items-center gap-2.5 text-sm font-semibold text-navy-900">
                <input type="checkbox" data-testid="p-call-pricing" checked={form.call_for_pricing} onChange={(e) => set("call_for_pricing", e.target.checked)} className="w-5 h-5 rounded border-slate-300 text-navy-900 focus:ring-navy-600" />
                Call for pricing (hide price)
              </label>

              {/* Tiered pricing */}
              <div>
                <label className={label}>Volume / tiered pricing</label>
                <div className="space-y-2">
                  {form.tiered_pricing.map((t, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`p-tier-${i}`}>
                      <input type="number" min="1" placeholder="Min qty" className={field} value={t.min_qty} onChange={(e) => updTier(i, "min_qty", e.target.value)} />
                      <span className="text-slate-400">@</span>
                      <input type="number" step="0.01" placeholder="Price ea" className={field} value={t.price} onChange={(e) => updTier(i, "price", e.target.value)} />
                      <button type="button" onClick={() => removeTier(i)} aria-label="Remove tier" className="shrink-0 w-11 h-11 grid place-items-center rounded-xl border border-red-300 text-red-600 hover:bg-red-500 hover:text-white"><Trash size={16} /></button>
                    </div>
                  ))}
                </div>
                <button type="button" data-testid="p-add-tier" onClick={addTier} className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-100"><Plus size={16} weight="bold" /> Add tier</button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Stock status</label>
                  <select data-testid="p-stock" className={field} value={form.stock_status} onChange={(e) => set("stock_status", e.target.value)}>{STOCK.map((s) => <option key={s}>{s}</option>)}</select>
                </div>
                <div className="flex items-end gap-4 pb-1">
                  <label className="flex items-center gap-2 text-sm font-semibold text-navy-900"><input type="checkbox" data-testid="p-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-5 h-5 rounded border-slate-300 text-navy-900 focus:ring-navy-600" /> Featured</label>
                  <label className="flex items-center gap-2 text-sm font-semibold text-navy-900"><input type="checkbox" data-testid="p-active" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 rounded border-slate-300 text-navy-900 focus:ring-navy-600" /> Active</label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Stock quantity (optional)</label><input type="number" min="0" data-testid="p-stock-qty" className={field} placeholder="Leave blank if not tracked" value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} /></div>
                <div><label className={label}>Low-stock alert below</label><input type="number" min="1" data-testid="p-low-threshold" className={field} value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} /></div>
              </div>
              <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                <input type="checkbox" data-testid="p-enforce-stock" checked={form.enforce_stock} onChange={(e) => set("enforce_stock", e.target.checked)} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-navy-900 focus:ring-navy-600" />
                <span>
                  <span className="block text-sm font-semibold text-navy-900">Limit orders to available stock</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Block checkout when a customer orders more than the stock quantity above. Requires a stock quantity to be set.</span>
                </span>
              </label>
              <div><label className={label}>Reorder target quantity (optional)</label><input type="number" min="0" data-testid="p-reorder-target" className={field} placeholder="e.g. restock up to 40" value={form.reorder_target} onChange={(e) => set("reorder_target", e.target.value)} /><p className="text-xs text-slate-500 mt-1">Used on the Reorder list to suggest how many to buy when this item runs low.</p></div>

              <button type="submit" disabled={saving} data-testid="p-save" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">{saving ? "Saving…" : editing === "new" ? "Add product" : "Save changes"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
